import type { SyncNaverPreview, SyncPreviewChange } from '../types/syncPreview';

// ③ 스토어 반영 확인 창 요약(감사 card-controltower#538, 백엔드 PR #76) — 응답 읽기 · 문구.

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/**
 * preview 응답 → SyncNaverPreview. 모양이 다르면(옛 서버 · 프록시 오류 페이지) null — 확인 창은 요약 없이 건수만 묻는다.
 * sendCount 가 숫자가 아니면 계약이 아니라고 본다. 나머지 칸은 없으면 0 · [].
 */
export function normalizeSyncPreview(raw: unknown): SyncNaverPreview | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.sendCount !== 'number') return null;
  const changes: SyncPreviewChange[] = Array.isArray(r.topChanges)
    ? r.topChanges
      .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
      .map((c) => ({
        id:                num(c.id),
        productName:       typeof c.productName === 'string' ? c.productName : '',
        sellerProductCode: typeof c.sellerProductCode === 'string' ? c.sellerProductCode : null,
        currentPrice:      num(c.currentPrice),
        suggestedPrice:    num(c.suggestedPrice),
        diff:              num(c.diff),
        rate:              num(c.rate),
        manual:            c.manual === true,
        overRiseCap:       c.overRiseCap === true,
      }))
      .slice(0, 5)
    : [];
  const blockedManualOverride = num(r.blockedManualOverride);
  const blockedHubRequest = num(r.blockedHubRequest);
  const blockedHubPending = num(r.blockedHubPending);
  return {
    requested:        num(r.requested),
    notNeeded:        num(r.notNeeded),
    sendCount:        r.sendCount,
    riseCount:        num(r.riseCount),
    dropCount:        num(r.dropCount),
    noBaseCount:      num(r.noBaseCount),
    overRiseCapCount: num(r.overRiseCapCount),
    riseCapRate:      typeof r.riseCapRate === 'number' && r.riseCapRate > 0 ? r.riseCapRate : 0.5,
    blockedCount:     typeof r.blockedCount === 'number' ? r.blockedCount : blockedManualOverride + blockedHubRequest + blockedHubPending,
    blockedManualOverride,
    blockedHubRequest,
    blockedHubPending,
    hubPendingChecked: r.hubPendingChecked !== false,
    hubPendingTruncated: r.hubPendingTruncated === true,
    estimatedSeconds: num(r.estimatedSeconds),
    topChanges:       changes,
  };
}

/** 예상 소요 — '약 40초' · '약 3분' · '약 1시간 5분'. 0 이하면 '1초 미만'. */
export function formatDuration(seconds: number): string {
  if (!(seconds > 0)) return '1초 미만';
  if (seconds < 60) return `약 ${Math.ceil(seconds)}초`;
  const min = Math.ceil(seconds / 60);
  if (min < 60) return `약 ${min}분`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `약 ${h}시간 ${m}분` : `약 ${h}시간`;
}

/** 변동률 — 0.25 → '+25%', −0.104 → '−10%'(1% 미만이라도 값이 다르면 ±1). */
export function formatRate(rate: number): string {
  if (rate === 0) return '0%';
  const pct = Math.round(Math.abs(rate) * 100) || 1;
  return `${rate > 0 ? '+' : '−'}${pct}%`;
}

/** 막힘 세 갈래 문구 — 0 인 갈래는 뺀다. */
export function blockedParts(p: SyncNaverPreview): string[] {
  return [
    p.blockedManualOverride > 0 ? `매장 직접 변경 확정 대기 ${p.blockedManualOverride.toLocaleString()}` : null,
    p.blockedHubRequest > 0 ? `PriceHub 요청 대기 ${p.blockedHubRequest.toLocaleString()}` : null,
    p.blockedHubPending > 0 ? `PriceHub 확정 대기 ${p.blockedHubPending.toLocaleString()}` : null,
  ].filter((s): s is string => !!s);
}
